import type { ArInvoiceOpenIssue } from '../ar-invoice-issues.js';
import type {
  ArInvoiceStatus,
  ArNfseCancellationStatus,
  ArWithholdingTaxType,
} from '../generated/enums.js';
import type { ExternalApiErrorCode, ExternalIngestionWarning, ExternalMetadata } from './bills.js';

/**
 * WorkOS org API key scopes for the External API invoice surface.
 *
 * These are `external:*` scopes checked by `RequireExternalScope` /
 * `ExternalApiKeyGuard` against `req.externalContext.permissions` — a WorkOS
 * org API key allowlist, unrelated to Platform JWT RBAC (`ar:read` /
 * `ar:create`, see ID-003/ID-024). Prior to this migration these routes used
 * the Platform RBAC slugs directly, which mixed the two auth planes; see
 * docs/agents/identity-decisions.md § ID-024.
 */
export const ExternalInvoiceScope = {
  READ: 'external:invoices:read',
  WRITE: 'external:invoices:write',
  /**
   * Lets `POST /v1/invoices` (and each bulk item) send `autoApprove: true`: the invoice is
   * approved right after it is created, through the ordinary approval (`issueInvoice`).
   */
  APPROVE: 'external:invoices:approve',
  /**
   * Lets `POST /v1/invoices/by-reference/:ref/cancel` send `cancelFiscalNote: true`: an
   * authorized NFS-e is cancelled with the municipality, and the invoice goes VOID when the
   * cancellation is confirmed.
   */
  CANCEL_FISCAL: 'external:invoices:cancel-fiscal',
} as const;
export type ExternalInvoiceScope = (typeof ExternalInvoiceScope)[keyof typeof ExternalInvoiceScope];

/** All External API invoice scopes (WorkOS org API key allowlist + route guards). */
export const EXTERNAL_API_INVOICE_SCOPES = [
  ExternalInvoiceScope.READ,
  ExternalInvoiceScope.WRITE,
  ExternalInvoiceScope.APPROVE,
  ExternalInvoiceScope.CANCEL_FISCAL,
] as const satisfies readonly ExternalInvoiceScope[];

/**
 * Payment intent an integrator reports alongside a charge (ALC-2235).
 *
 * `PENDING` is the ordinary case: the invoice runs the normal lifecycle and
 * waits for its money. `PAID` means the integrator's source system has already
 * captured the charge on a payment gateway — the invoice still runs the FULL
 * normal lifecycle first (DRAFT -> ISSUED, with GL posting and classification)
 * and only then lands in `PAID_UNCONFIRMED`. It is never a status shortcut.
 */
export const ExternalInvoicePaymentStatus = {
  PENDING: 'PENDING',
  PAID: 'PAID',
} as const;
export type ExternalInvoicePaymentStatus =
  (typeof ExternalInvoicePaymentStatus)[keyof typeof ExternalInvoicePaymentStatus];

/** Maximum length of `ArInvoice.externalPaymentRefId` (`@db.VarChar(255)`). */
export const EXTERNAL_PAYMENT_REFERENCE_MAX_LENGTH = 255;

/**
 * Minimum alphanumeric characters a gateway transaction id must have, counted AFTER
 * `normalizeExternalPaymentReference` strips separators and upper-cases.
 *
 * This is the reconciliation matcher's floor, not a stylistic one: the AR0 rule searches
 * the bank credit's memo for the reference and refuses anything shorter, because a handful
 * of characters collides with ordinary memo words and a false high-confidence match
 * settles the wrong receivable. Accepting a shorter reference at the API boundary would
 * therefore mint invoices carrying a key that can never locate their credit — the
 * receivable would stay open in aging while the cash forecast projected money already
 * received. Both sides MUST use this constant.
 */
export const EXTERNAL_PAYMENT_REFERENCE_MIN_ALNUM = 8;

/**
 * The comparison form of a gateway transaction id: upper-cased, separators removed.
 *
 * Used on both sides of the AR0 match, so `ch_9aK2mQ7xLp` on the invoice and
 * `CH-9AK2 MQ7XLP` in a bank memo reduce to the same string, and used at the API boundary
 * to measure a reference against {@link EXTERNAL_PAYMENT_REFERENCE_MIN_ALNUM}.
 */
export function normalizeExternalPaymentReference(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Maximum length of an invoice `externalReferenceId`, inherited from
 * `ArInvoice.externalRef` (`@db.VarChar(100)`). Also bounds the bulk route's
 * `Idempotency-Key` header.
 */
export const EXTERNAL_INVOICE_REFERENCE_MAX_LENGTH = 100;

/**
 * Payment rails an integrator may report a captured charge on. A subset of the
 * internal `PaymentMethodType` enum, restricted to what a gateway can actually
 * report.
 */
export const ExternalInvoicePaymentMethod = {
  CREDIT_CARD: 'CREDIT_CARD',
  DEBIT_CARD: 'DEBIT_CARD',
  PIX: 'PIX',
  BOLETO: 'BOLETO',
  TED: 'TED',
  OTHER: 'OTHER',
} as const;
export type ExternalInvoicePaymentMethod =
  (typeof ExternalInvoicePaymentMethod)[keyof typeof ExternalInvoicePaymentMethod];

/**
 * Proof that the caller knows WHICH customer an invoice belongs to.
 *
 * The by-reference mutation routes address an invoice by a reference the
 * integrator chose, and UI-created invoices carry guessable sequential numbers.
 * Without corroboration an integrator key could cancel an unrelated internal
 * invoice by guessing a reference. Mirrors the contracts slice (ALC-2234).
 */
export interface ExternalInvoiceCustomerCorroboration {
  /** The customer's AR code. */
  customerReferenceId?: string;
  /** CNPJ or CPF digits, formatted or raw. */
  customerTaxId?: string;
}

/** Body of `POST /v1/invoices/by-reference/:ref/mark-paid`. */
export interface ExternalMarkInvoicePaidRequest extends ExternalInvoiceCustomerCorroboration {
  /** Gateway transaction id — the key reconciliation matches the bank credit on. */
  externalPaymentRefId: string;
  /** When the gateway captured the charge (ISO-8601). */
  paidAt: string;
  paymentMethod?: ExternalInvoicePaymentMethod;
  /** Disambiguates a reference that exists under more than one entity. */
  entityId?: string;
}

/** Body of `POST /v1/invoices/by-reference/:ref/cancel`. */
export interface ExternalCancelInvoiceRequest extends ExternalInvoiceCustomerCorroboration {
  /** Free-text reason recorded on the invoice history. */
  reason?: string;
  /** Disambiguates a reference that exists under more than one entity. */
  entityId?: string;
  /**
   * Also cancel the invoice's authorized NFS-e (needs scope `external:invoices:cancel-fiscal`).
   * The cancellation is filed with the municipality asynchronously; the invoice goes VOID when
   * it is confirmed. Without an authorized NFS-e, the ordinary cancel runs.
   */
  cancelFiscalNote?: boolean;
}

/** The NFS-e cancellation a `cancelFiscalNote` cancel requested (or found already in progress). */
export interface ExternalFiscalNoteCancellation {
  cancellationId: string;
  /** The invoice is VOID once this is CONFIRMED. */
  status: ArNfseCancellationStatus;
}

export interface ExternalCreateInvoiceWithholding {
  taxType: ArWithholdingTaxType;
  /** Decimal string (accepts BR formats like bill amount). */
  amount: string;
}

export interface ExternalCreateInvoiceRequest {
  entityId: string;
  externalReferenceId: string;
  issueDate: string;
  grossAmount: string;
  netAmount: string;
  /**
   * CNPJ or CPF — triggers resolve-or-create when customer does not exist. A value that is not a
   * valid CPF/CNPJ is kept: the invoice is created as a DRAFT with the issue
   * `CUSTOMER_TAX_ID_INVALID` when its NFS-e is issued by Alchemy ERP (ALC-5184).
   */
  customerTaxId?: string;
  customerId?: string;
  /**
   * The customer's code in your system. An unknown code creates the customer from it and
   * `customerLegalName` (required then), as a person with no document (ALC-5184).
   */
  customerCode?: string;
  /** Required when the customer is created: by `customerTaxId`, or by an unknown `customerCode`. */
  customerLegalName?: string;
  invoiceNumber?: string;
  dueDate?: string;
  currency?: string;
  description?: string;
  serviceCode?: string;
  withholdings?: ExternalCreateInvoiceWithholding[];
  /**
   * Subscription contract this charge belongs to (ALC-2235), addressed the same
   * way `/v1/contracts/by-reference/:ref` addresses it: by
   * `BillingContract.externalReferenceId`, falling back to `code`.
   *
   * Every charge for one subscription — renewal, installment, recovery retry —
   * carries the same value, which is what lets finance roll invoices back up to
   * the subscription. The contract's customer must agree with the invoice's, or
   * the request is refused.
   */
  contractReferenceId?: string;

  /**
   * OPT-IN marker: "this invoice IS the full recurring charge for the contract cycle
   * beginning on this date" (ISO `YYYY-MM-DD`). Only valid together with
   * `contractReferenceId` (ALC-2801, accounting gate CFO-1 / rule CFO-02).
   *
   * It is what tells the cash forecast to stop projecting that cycle, because the real
   * invoice for it now exists. Omit it for anything that is NOT the whole recurring
   * charge — a one-off, an add-on, a partial or a top-up — otherwise the projection for a
   * cycle that has not been billed yet disappears and the forecast understates cash.
   *
   * Idempotent by construction: a recovery retry for the same occurrence re-sends the same
   * value and therefore produces the same supersession key.
   */
  billingPeriodStart?: string;
  /**
   * Payment intent. Defaults to `PENDING`. `PAID` runs the full lifecycle and
   * then lands the invoice in `PAID_UNCONFIRMED`; it requires `paidAt` and
   * `externalPaymentRefId`.
   */
  paymentStatus?: ExternalInvoicePaymentStatus;
  /** When the gateway captured the charge (ISO-8601). Required with `paymentStatus: PAID`. */
  paidAt?: string;
  paymentMethod?: ExternalInvoicePaymentMethod;
  /**
   * Gateway transaction id (e.g. a Pagar.me charge id). Required with
   * `paymentStatus: PAID` — it is the join key bank reconciliation matches the
   * incoming credit on to promote the invoice to `PAID`.
   */
  externalPaymentRefId?: string;
  /**
   * Flat integrator metadata (ALC-2236) — e.g. `{ coupon: "BLACKFRIDAY2026",
   * salesChannel: "web", offerType: "annual" }`. On a replay (same
   * `externalReferenceId` as an existing invoice), omitting this field leaves the
   * invoice's stored metadata untouched — it is never cleared implicitly. Send `{}`
   * explicitly to replace it with an empty object. `metadata: null` is rejected with
   * `METADATA_INVALID`, same as any other non-object value — omit the field instead.
   */
  metadata?: ExternalMetadata;
  /**
   * The invoice's PAYMENT SCHEDULE — how its one total is collected over time (ALC-2824).
   *
   * Not a way to create several invoices. A 12x card subscription is ONE invoice with ONE
   * `externalPaymentRefId`: the customer's card was charged once, the receivable is
   * against the card company, and these are the dates the acquirer remits on. That is why
   * a PAID intent with installments still lands the invoice in `PAID_UNCONFIRMED` rather
   * than `PAID` — the charge is captured, the cash has not arrived.
   *
   * Omit it and the invoice keeps the single-installment schedule the ordinary import
   * writes (the whole collectible total due on `dueDate`).
   */
  installments?: ExternalInvoiceInstallmentSpec;
  /**
   * The NF your own system issued for the services set to issue the NF outside Alchemy ERP
   * (ALC-5091): its number and its issue date (ISO `YYYY-MM-DD`), both or neither. Without
   * them, the invoice waits as "NF externa pendente" until the NF is linked.
   */
  externalFiscalNoteNumber?: string;
  externalFiscalNoteIssueDate?: string;
  /**
   * Approve the invoice right after it is created (needs scope `external:invoices:approve`).
   * The approval is the ordinary one: an Alchemy-issued NFS-e is requested and the invoice
   * waits in AWAITING_NF. A refused approval leaves the invoice DRAFT or BLOCKED and adds the
   * warning `AUTO_APPROVE_FAILED`; the create still succeeds. A draft with open issues is never
   * approved: the warning is `AUTO_APPROVE_SKIPPED_OPEN_ISSUES`, and a resend that fixes the
   * issues approves it (ALC-5184).
   */
  autoApprove?: boolean;
}

/**
 * How to build an invoice's `ArPaymentSchedule` rows (ALC-2824). EXACTLY ONE of the two
 * forms, never both and never neither — violations are `INSTALLMENT_SPEC_INVALID`.
 *
 * GENERATED (`count` + `firstDueDate` + `intervalDays`): Alchemy splits the collectible
 * total. The split is exact by construction — every installment but the last is floored to
 * the currency's minor unit and the last absorbs the remainder — so it can never drift
 * from the invoice total by a centavo. Prefer this form; it cannot raise
 * `INSTALLMENT_TOTAL_MISMATCH`.
 *
 * EXPLICIT (`rows`): the integrator states each parcel. Use it when the acquirer's own
 * split is not a clean even division, or when the dates are irregular. The amounts must
 * sum EXACTLY to the collectible total or the charge is refused.
 */
export interface ExternalInvoiceInstallmentSpec {
  /** Number of parcels to generate. Integer, 2..60. Generated form only. */
  count?: number;
  /** Due date of parcel 1 (ISO `YYYY-MM-DD`). Generated form only. */
  firstDueDate?: string;
  /** Days between consecutive parcels. Integer, 1..366. Generated form only. */
  intervalDays?: number;
  /** Explicit parcels. 1..60 rows, `installmentNumber` exactly 1..N. Explicit form only. */
  rows?: ExternalInvoiceInstallmentRow[];
}

/** One explicitly-stated parcel of an invoice's payment schedule (ALC-2824). */
export interface ExternalInvoiceInstallmentRow {
  /** 1-based position. The set across `rows` must be exactly 1..N — no gaps, no repeats. */
  installmentNumber: number;
  /** ISO `YYYY-MM-DD`. */
  dueDate: string;
  /**
   * Decimal string, greater than zero. Summed EXACTLY (never rounded, never compared with
   * a float epsilon) against the invoice's collectible total.
   */
  amount: string;
}

/**
 * One parcel of an invoice's payment schedule as reported on a GET (ALC-2824).
 *
 * `paid` is what makes settlement progress visible: an invoice in `PARTIALLY_RECEIVED`
 * shows the settled parcels with `paid` equal to `amount` and `settled: true`, and the
 * outstanding ones with `paid: "0.00"`. Reading it is how an integrator answers "which of
 * my 12 parcels has the acquirer actually remitted?".
 *
 * Only LIVE parcels appear. One retired by a customer credit, or belonging to a VOID
 * invoice, is omitted rather than reported as settled — no cash is coming for it, and the
 * rest of Alchemy (cash-flow projection, receipt allocation) does not expect any either.
 * The invoice's `netAmount` stays the authority on the total.
 */
export interface ExternalInvoiceInstallmentState {
  installmentNumber: number;
  dueDate: string;
  /** The parcel's share of the collectible total. */
  amount: string;
  /** Cash allocated to THIS parcel so far. `"0.00"` when nothing has settled against it. */
  paid: string;
  /**
   * `paid >= amount`, compared as exact decimals — i.e. `settled` reflects the allocations
   * recorded against THIS parcel. It is a statement about what has been booked to the
   * parcel, not a promise about which parcel a given receipt was intended for: how incoming
   * cash is distributed across a schedule is the allocation flow's decision, and this field
   * reports its outcome.
   */
  settled: boolean;
}

export interface ExternalInvoiceResponse {
  id: string;
  status: ArInvoiceStatus;
  entityId: string;
  customerId: string;
  invoiceNumber: string;
  externalReferenceId: string;
  grossAmount: string;
  netAmount: string;
  issueDate: string;
  dueDate?: string | null;
  createdAt: string;
  warnings?: ExternalIngestionWarning[];
  /** Echoed back so an integrator can confirm the charge bound to the intended subscription. */
  contractReferenceId?: string | null;
  /**
   * Echoed back so the caller can confirm whether this charge suppresses the contract
   * cycle's forecast projection (ALC-2801). Null means it does not.
   */
  billingPeriodStart?: string | null;
  externalPaymentRefId?: string | null;
  /** ISO-8601 gateway capture instant. Not a settlement date and not a GL date. */
  paidAt?: string | null;
  paymentMethod?: ExternalInvoicePaymentMethod | null;
  /** Echoed back verbatim (ALC-2236). Null when the invoice carries none. */
  metadata?: ExternalMetadata | null;
  /**
   * The invoice's payment schedule with per-parcel settlement state (ALC-2824), ordered by
   * `installmentNumber`.
   *
   * Always present for an invoice that has schedule rows — which, after an ordinary
   * import, is every invoice: one row for the whole collectible total. An invoice created
   * with an `installments` spec reports N rows here instead.
   */
  installments?: ExternalInvoiceInstallmentState[];
  /** Present only on a `cancelFiscalNote` cancel that requested an NFS-e cancellation. */
  fiscalNoteCancellation?: ExternalFiscalNoteCancellation;
  /**
   * The open issues of a draft (ALC-5184): what the source must fix before it can be approved.
   * Empty when there are none.
   */
  issues?: ArInvoiceOpenIssue[];
}

export interface ExternalBulkCreateInvoicesRequest {
  invoices: ExternalCreateInvoiceRequest[];
}

export interface ExternalBulkCreateInvoiceResultError {
  code: ExternalApiErrorCode;
  message: string;
}

export interface ExternalBulkCreateInvoiceResult {
  externalReferenceId: string;
  created: boolean;
  invoice?: ExternalInvoiceResponse;
  error?: ExternalBulkCreateInvoiceResultError;
}

export interface ExternalBulkCreateInvoicesResponse {
  results: ExternalBulkCreateInvoiceResult[];
}
